import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import JSLayout from "./js_layout";
import { API_ENDPOINTS } from "../../api/config";

const css = `
  .ma-filter-row { display: flex; gap: 0.5rem; margin-bottom: 1.5rem; flex-wrap: wrap; }
  .ma-filter-btn { padding: 0.5rem 1.1rem; border-radius: 20px; border: 1px solid rgba(26,26,26,0.12); background: #fff; font-family:'DM Sans',sans-serif; font-size:0.75rem; color:#888; cursor:pointer; transition:all 0.2s; }
  .ma-filter-btn.active { background:#1a1a1a; color:#f5f0e8; border-color:#1a1a1a; }
  .ma-stats-row { display:grid; grid-template-columns:repeat(5,1fr); gap:0.75rem; margin-bottom:1.75rem; }
  .ma-stat { background:#fff; border:1px solid rgba(26,26,26,0.07); border-radius:4px; padding:1rem 1.1rem; }
  .ma-stat-n { font-family:'Cormorant Garamond',serif; font-size:1.8rem; font-weight:600; color:#1a1a1a; line-height:1; }
  .ma-stat-l { font-size:0.62rem; font-weight:500; letter-spacing:0.08em; text-transform:uppercase; color:#bbb; margin-top:3px; }
  .ma-list { display:flex; flex-direction:column; gap:0.75rem; }
  .ma-card { background:#fff; border:1px solid rgba(26,26,26,0.07); border-radius:4px; padding:1.25rem 1.5rem; display:grid; grid-template-columns:46px 1fr auto; gap:1rem; align-items:center; cursor:pointer; transition:all 0.2s; }
  .ma-card:hover { border-color:rgba(201,168,76,0.3); box-shadow:0 2px 12px rgba(201,168,76,0.06); }
  .ma-logo { width:46px; height:46px; border-radius:4px; background:#f5f0e8; border:1px solid rgba(26,26,26,0.07); display:flex; align-items:center; justify-content:center; font-size:1.2rem; flex-shrink:0; }
  .ma-info { min-width:0; }
  .ma-title { font-size:0.92rem; font-weight:500; color:#1a1a1a; margin-bottom:2px; }
  .ma-company { font-size:0.78rem; color:#aaa; font-weight:300; margin-bottom:6px; }
  .ma-tags { display:flex; gap:5px; flex-wrap:wrap; }
  .ma-right { display:flex; flex-direction:column; align-items:flex-end; gap:6px; flex-shrink:0; }
  .ma-date { font-size:0.72rem; color:#bbb; }
  .ma-timeline { margin-top:1rem; display:flex; align-items:center; gap:0; }
  .ma-step { display:flex; flex-direction:column; align-items:center; flex:1; }
  .ma-step-dot { width:10px; height:10px; border-radius:50%; background:rgba(26,26,26,0.1); margin-bottom:5px; transition:background 0.2s; }
  .ma-step-dot.done { background:#c9a84c; }
  .ma-step-dot.current { background:#1a1a1a; box-shadow:0 0 0 3px rgba(26,26,26,0.12); }
  .ma-step-dot.rejected { background:#e74c3c; }
  .ma-step-label { font-size:0.58rem; color:#bbb; text-align:center; letter-spacing:0.04em; }
  .ma-step-line { flex:1; height:1px; background:rgba(26,26,26,0.08); margin-bottom:14px; }
  .ma-step-line.done { background:#c9a84c; }
  .ma-empty { text-align:center; padding:4rem 2rem; }
  .ma-empty-icon { font-size:2.5rem; margin-bottom:1rem; }
  .ma-empty-title { font-family:'Cormorant Garamond',serif; font-size:1.4rem; font-weight:600; color:#1a1a1a; margin-bottom:0.5rem; }
  .ma-empty-sub { font-size:0.85rem; color:#aaa; font-weight:300; }
  @media(max-width:768px){
    .ma-stats-row { grid-template-columns:repeat(3,1fr); }
    .ma-card { grid-template-columns:40px 1fr; }
    .ma-right { display:none; }
    .ma-card-mobile-status { display:flex; justify-content:space-between; align-items:center; margin-top:8px; padding-top:8px; border-top:1px solid rgba(26,26,26,0.05); }
  }
  @media(max-width:500px){
    .ma-stats-row { grid-template-columns:1fr 1fr; }
    .ma-timeline { display:none; }
  }
`;

const STATUS_STEPS = {
  applied:   { step: 0, label: "Applied" },
  review:    { step: 1, label: "Under Review" },
  interview: { step: 2, label: "Interview" },
  offered:   { step: 3, label: "Offered" },
  rejected:  { step: -1, label: "Not Selected" },
};

const STEPS = ["Applied", "Under Review", "Interview", "Offered"];
const FILTERS = ["All", "Applied", "Under Review", "Interview", "Offered", "Not Selected"];

const pillClass = { applied: "st-applied", review: "st-review", interview: "st-interview", offered: "st-offered", rejected: "st-rejected" };
const pillLabel = { applied: "Applied", review: "Under Review", interview: "Interview Scheduled", offered: "Offer Received", rejected: "Not Selected" };

export default function MyApplications() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const [appsData, setAppsData] = useState({
    applications: [],
    totalCount: 0,
    activeCount: 0,
    interviewCount: 0,
    offeredCount: 0,
    rejectedCount: 0
  });

  // Fetch applications from backend database pool
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(API_ENDPOINTS.EMPJOBAPPS, {
          headers: { "Authorization": `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setAppsData(data);
        }
      } catch (error) {
        // console.error("Error connecting to database applications", error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  const filtered = filter === "All" 
    ? appsData.applications 
    : appsData.applications.filter(a => pillLabel[a.status] === filter || (filter === "Not Selected" && a.status === "rejected"));


  if (typeof document !== "undefined" && !document.getElementById("bl-ma-css")) {
    const s = document.createElement("style"); s.id = "bl-ma-css"; s.textContent = css; document.head.appendChild(s);
  }

    if (loading) return <JSLayout title="Loading..."><div>Loading Applications...</div></JSLayout>;

  return (
    <JSLayout title="My" titleEm="Applications">
      {/* Dynamic Summary Cards Row */}
      <div className="ma-stats-row">
        {[
          ["Total", appsData.totalCount],
          ["Active", appsData.activeCount],
          ["Interview", appsData.interviewCount],
          ["Offered", appsData.offeredCount],
          ["Rejected", appsData.rejectedCount]
        ].map(([l, n]) => (
          <div key={l} className="ma-stat">
            <div className="ma-stat-n">{n}</div>
            <div className="ma-stat-l">{l}</div>
          </div>
        ))}
      </div>

      {/* Interactive Quick Filter bar */}
      <div className="ma-filter-row">
        {FILTERS.map(f => (
          <button 
            key={f} 
            className={`ma-filter-btn${filter === f ? " active" : ""}`} 
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Dynamic Main Listing Container */}
      {filtered.length === 0 ? (
        <div className="ma-empty">
          <div className="ma-empty-icon">📋</div>
          <div className="ma-empty-title">No applications here</div>
          <div className="ma-empty-sub">Try a different filter category or browse new opportunities</div>
        </div>
      ) : (
        <div className="ma-list">
          {filtered.map(app => {
            const s = STATUS_STEPS[app.status] || { step: 0, label: "Applied" };
            return (
              <div key={app.id} className="ma-card" onClick={() => navigate(`/jobs/${app.id}`)}>
                <div className="ma-logo">{app.icon}</div>
                <div className="ma-info">
                  <div className="ma-title">{app.title}</div>
                  <div className="ma-company">{app.company}</div>
                  <div className="ma-tags">
                    <span className="bl-tag bl-tag-loc">📍 {app.location}</span>
                    <span className="bl-tag bl-tag-type">{app.type}</span>
                  </div>
                  
                  {/* Dynamic Interactive Timeline */}
                  {app.status !== "rejected" ? (
                    <div className="ma-timeline" style={{ marginTop: "0.85rem" }}>
                      {STEPS.map((step, i) => (
                        <div key={step} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? "1" : "0" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <div className={`ma-step-dot${i < s.step ? " done" : i === s.step ? " current" : ""}`} />
                            <span className="ma-step-label">{step}</span>
                          </div>
                          {i < STEPS.length - 1 && <div className={`ma-step-line${i < s.step ? " done" : ""}`} />}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="ma-card-mobile-status" style={{ marginTop: "0.6rem" }}>
                      <span style={{ fontSize: "0.78rem", color: "#666", fontStyle: "italic" }}>{app.note}</span>
                    </div>
                  )}
                </div>
                
                <div className="ma-right">
                  <div className="ma-date">{app.date}</div>
                  <span className={`bl-status-pill ${pillClass[app.status] || 'st-applied'}`}>
                    {pillLabel[app.status] || 'Applied'}
                  </span>
                  <span style={{ fontSize: "0.75rem", color: "#888", fontWeight: 400, textAlign: "right", maxWidth: "160px" }}>
                    {app.note}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </JSLayout>
  );
}